import { ITourContext } from "training/types";

/**
 * CLE-3015 — the branch conditions of Modules 1–2 (curriculum "Branch points"), as pure functions of the
 * tour context. They never read the DOM: whether an element is on screen is driver.js's job
 * (`skipMissingElement`), and an element that is present but hidden would fool a DOM check anyway.
 */

/** The name of the self-pay payer row ("Self-Pay Rate"); it exists only where a hospital configured one. */
export const SELF_PAY_PAYER_NAME = "Self-Pay Rate";

/** 1.2 — the facility switcher renders only for users with more than one hospital. */
export const hasMultipleAccounts = (ctx: ITourContext) => ctx.accountCount > 1;

/**
 * 1.8 / 1.9 — the hospital has at least one estimate, so the Estimates table has a first row with Collect
 * Payment, View and the row menu. Unknown (not loaded) counts as none.
 */
export const hasEstimates = (ctx: ITourContext) => (ctx.estimateCount ?? 0) > 0;

/** 1.10 — Direct Pay is hidden unless enabled for the hospital. */
export const directPayEnabled = (ctx: ITourContext) =>
  !!ctx.account?.directPayEnabled;

/** 2.2 — the hospital shows the Patient Estimate order type (and so the Insurance & Benefits card). */
export const offersPatientEstimate = (ctx: ITourContext) =>
  ctx.account?.orderTypesVisible?.gfe === true;

/** 2.2 — no Patient Estimate: Module 2 runs the Patient Balance variant (Patient, Visit, a balance, send). */
export const usesPatientBalanceVariant = (ctx: ITourContext) =>
  !offersPatientEstimate(ctx) &&
  ctx.account?.orderTypesVisible?.patientResponsibility === true;

/** 2.6 / 2.7 — Service Type, Member ID and Run Eligibility render only when the main facility runs eligibility. */
export const eligibilityEnabled = (ctx: ITourContext) =>
  offersPatientEstimate(ctx) && !!ctx.account?.eligibilityEnabled;

/** 2.5 — "Self-Pay Rate" is in this hospital's payer list. Unknown (not loaded) counts as absent. */
export const hasSelfPayPayer = (ctx: ITourContext) =>
  !!ctx.payerNames?.includes(SELF_PAY_PAYER_NAME);

/** The order type the tour has the trainee build, as the account's per-type settings key it. */
export const tourOrderTypeKey = (ctx: ITourContext) => {
  if (offersPatientEstimate(ctx)) return "gfe";
  if (usesPatientBalanceVariant(ctx)) return "patientResponsibility";
  return "bundled";
};

/**
 * 2.13 — the Payment Options card renders when the account allows plans for this order type or allows
 * partial payments (PaymentPlan.tsx returns null otherwise).
 */
export const paymentOptionsShown = (ctx: ITourContext) =>
  !!ctx.account?.partialPaymentsEnabled ||
  !!ctx.account?.paymentPlanTypeEnabled?.[tourOrderTypeKey(ctx)];

/** 2.16 — the trainee's estimate was created at $0, so its status is No Balance Due, not Sent. */
export const orderIsNoBalanceDue = (ctx: ITourContext) =>
  ctx.order?.status === "NoBalanceDue" || !!ctx.noBalanceDue;

/** 4.6 — the estimate is on a Finix hospital: the surcharge sentence and the "Payment method fees" box. */
export const usesFinix = (ctx: ITourContext) =>
  ctx.account?.paymentProcessor === "finix";

/** 6.5 — the user can download paid estimates by date (the per-user analytics setting, ReportsTable.tsx). */
export const hasAnalytics = (ctx: ITourContext) => ctx.analyticsTabEnabled;

// --- Modules 3–6: the trainee's own estimate ------------------------------------------------------------

export const ORDER_STATUS_SENT = "SentToPatient";

/** The estimate's order type as the account's per-type settings key it (CreateOrder/utils/helpers.ts). */
const ORDER_TYPE_KEYS: Record<string, string> = {
  GFE: "gfe",
  Bundled: "bundled",
  ED: "ed",
  PatientResponsibility: "patientResponsibility",
  Bariatrics: "bariatrics",
};

/** The trainee's estimate's type key; before it is known, the type Module 2 had them build. */
export const orderTypeKey = (ctx: ITourContext) =>
  (ctx.order?.orderType && ORDER_TYPE_KEYS[ctx.order.orderType]) ||
  tourOrderTypeKey(ctx);

/** The estimate is Sent (texted, unpaid): Collect Payment, Mark paid and Resend Notification show only then. */
export const orderIsSent = (ctx: ITourContext) =>
  ctx.order?.status === ORDER_STATUS_SENT && !orderIsNoBalanceDue(ctx);

/**
 * 4.1–4.9 — Collect Payment and the patient page's payment steps. Not on a $0 (No Balance Due) estimate —
 * the curriculum's branch skips 4.1 to 4.9 — nor on one that is no longer Sent (the button is gone).
 */
export const collectsPayment = orderIsSent;

/** 4.11 — "Mark paid via cash": a Sent estimate on a hospital with either cash flag (OrderHeader.tsx). */
export const marksPaidViaCash = (ctx: ITourContext) =>
  orderIsSent(ctx) &&
  (!!ctx.account?.features?.markPaidViaCash ||
    !!ctx.account?.features?.markPaidViaCashUnmanaged);

/** 4.11 — "Mark paid via check", pointed at only when there is no cash button to point at instead. */
export const marksPaidViaCheckOnly = (ctx: ITourContext) =>
  orderIsSent(ctx) &&
  !!ctx.account?.features?.markPaidViaCheck &&
  !marksPaidViaCash(ctx);

/**
 * 4.11 — "Mark paid via cash" or "Mark paid via check": a Sent estimate on a hospital with any of the three
 * flags. The step highlights the group the "..." menu wraps them in (OrderHeader.tsx), whichever it holds.
 */
export const marksPaid = (ctx: ITourContext) =>
  marksPaidViaCash(ctx) ||
  (orderIsSent(ctx) && !!ctx.account?.features?.markPaidViaCheck);

/** 3.9 — "Download Good Faith Estimate (PDF)" is not offered on Patient Balance estimates. */
export const offersPdf = (ctx: ITourContext) =>
  orderTypeKey(ctx) !== "patientResponsibility";

/** 3.9 — "Download ABN" appears when an ABN applies to a code on the estimate. */
export const abnApplies = (ctx: ITourContext) => !!ctx.order?.abnApplies;

/** 3.6 — the Insurance & Benefits card is on Patient Estimate (GFE) estimates only. */
export const showsBenefits = (ctx: ITourContext) => orderTypeKey(ctx) === "gfe";

/**
 * 4.4 — the patient page's "Pay in Full" / "Payment Plan" switch, as a staff user sees it (Home.tsx): an
 * active plan, plans allowed for this estimate type, or partial payments.
 */
export const showsPlanSwitch = (ctx: ITourContext) =>
  !!ctx.order?.planEnabled ||
  !!ctx.account?.paymentPlanTypeEnabled?.[orderTypeKey(ctx)] ||
  !!ctx.account?.partialPaymentsEnabled;

/**
 * 5.1–5.3 — the Estimate Details "Payment Plan" card on an unpaid estimate (OrderView.tsx showPaymentPlan /
 * showDownpaymentOnly): an active plan, plans allowed for this type, or partial payments on a Sent estimate.
 * Its pencil is hidden on No Balance Due, and the curriculum skips 5.1 there, so the three go together.
 */
export const showsPlanCard = (ctx: ITourContext) =>
  !orderIsNoBalanceDue(ctx) &&
  (!!ctx.order?.planEnabled ||
    !!ctx.account?.paymentPlanTypeEnabled?.[orderTypeKey(ctx)] ||
    (!!ctx.account?.partialPaymentsEnabled && orderIsSent(ctx)));

/** 4.3 — the account shows the prompt-pay discount on the patient page ("Save N%", "N% off"). Unknown → shown. */
export const showsDiscount = (ctx: ITourContext) =>
  ctx.order?.discountShown !== false;

/** 4.3 — …and "Discount expires on <date>", which needs a scheduled visit. */
export const showsDiscountExpiry = (ctx: ITourContext) =>
  showsDiscount(ctx) &&
  !!ctx.order?.discountExpiryShown &&
  ctx.order?.scheduled !== false;

/**
 * 4.5 — "Continue" is on the page: the account has not turned it off, and does not hide it while the visit
 * is not scheduled (Home.tsx showContinueButton).
 */
export const showsContinue = (ctx: ITourContext) => {
  const features = ctx.account?.features;
  if (!features?.checkoutContinueButtonVisible) return false;
  return !(
    features.hideCheckoutWhenNotScheduled && ctx.order?.scheduled === false
  );
};

/** 4.5 — "Continue" is disabled while an ABN the estimate needs is unsigned (Home.tsx continueButtonDisabled). */
export const continueBlockedByAbn = (ctx: ITourContext) =>
  !!ctx.order?.abnApplies && !ctx.order?.abnSigned;

/** 4.5–4.9 — the trainee can reach checkout. */
export const reachesCheckout = (ctx: ITourContext) =>
  showsContinue(ctx) && !continueBlockedByAbn(ctx);

/** 5.7 — "Send Custom Text" is hidden when the patient is email-only. Unknown → shown. */
export const textsPatient = (ctx: ITourContext) =>
  ctx.order?.textsPatient !== false;

/** 6.3 and the Module 6 card — the Add Note dialog offers "This is a payment note." (AddNoteDialog.tsx). */
export const paymentNotesEnabled = (ctx: ITourContext) =>
  !!ctx.account?.features?.orderPaymentNotesEnabled;

export const not =
  (condition: (ctx: ITourContext) => boolean) => (ctx: ITourContext) =>
    !condition(ctx);

export const all =
  (...conditions: Array<(ctx: ITourContext) => boolean>) =>
  (ctx: ITourContext) =>
    conditions.every((condition) => condition(ctx));
